/**
 * Places advanced-filter document (Phase 5 M5.2) — a versioned value the web
 * filter panel edits and evaluates CLIENT-SIDE over the complete place index.
 * One schema + ONE evaluator, both in @releve/shared, shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyPlaceFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The document is AD HOC — it lives in component state for the session. It was
 * briefly persisted to a `place_filter_presets` table; saved/shared presets
 * were removed on 2026-08-10 (too much machinery for this phase) and the
 * table with them, but the schema and evaluator are unchanged, so adding
 * persistence back later is a storage question, not a redesign.
 *
 * v1 fields are `place.<column>` over `place_index` columns only. The
 * `detail.<key>` namespace (place_details_current-backed fields) is RESERVED
 * for v1.1 and deliberately rejected by this schema — the document stays
 * forward-compatible because the field string carries its namespace.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const PLACE_FILTER_STRING_FIELDS = [
    "place.premises_level",
    "place.ownership_status",
    "place.primary_bedes_occupancy_classification",
    "place.country_code",
    "place.city",
    "place.external_source",
];
export const PLACE_FILTER_NUMERIC_FIELDS = [
    "place.gross_floor_area_m2",
    "place.sub_place_count",
];
export const PLACE_FILTER_BOOLEAN_FIELDS = [
    "place.is_active",
    "place.has_address",
];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
/** `in` = value is one of `values` (exact match); `is_empty` = value unset. */
const stringConditionSchema = z
    .object({
    field: z.enum(PLACE_FILTER_STRING_FIELDS),
    op: z.enum(["in", "is_empty"]),
    values: z.array(z.string().min(1).max(200)).min(1).max(50).optional(),
})
    .refine((c) => c.op !== "in" || (c.values?.length ?? 0) > 0, {
    message: "op 'in' requires a non-empty values array",
});
/** Numeric range: `between` uses both bounds, `gte`/`lte` one. NULL never matches. */
const numericConditionSchema = z
    .object({
    field: z.enum(PLACE_FILTER_NUMERIC_FIELDS),
    op: z.enum(["gte", "lte", "between"]),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
})
    .refine((c) => (c.op === "gte" && c.min != null) ||
    (c.op === "lte" && c.max != null) ||
    (c.op === "between" && c.min != null && c.max != null), { message: "numeric condition is missing its bound(s)" });
const booleanConditionSchema = z.object({
    field: z.enum(PLACE_FILTER_BOOLEAN_FIELDS),
    op: z.literal("eq"),
    value: z.boolean(),
});
export const placeFilterConditionSchema = z.union([
    stringConditionSchema,
    numericConditionSchema,
    booleanConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const placeFilterDocumentSchema = z.object({
    version: z.literal(1),
    /** v1 is AND-only; `match` exists so v2 can add 'any' without re-shaping. */
    match: z.literal("all"),
    conditions: z.array(placeFilterConditionSchema).max(20),
});
/** The empty document — "no filters active". */
export const EMPTY_PLACE_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
// ---------------------------------------------------------------------------
// The evaluator — the ONE implementation of document semantics
// ---------------------------------------------------------------------------
function stringValue(row, field) {
    switch (field) {
        case "place.premises_level":
            return row.premises_level;
        case "place.ownership_status":
            return row.ownership_status ?? "";
        case "place.primary_bedes_occupancy_classification":
            return row.primary_bedes_occupancy_classification ?? "";
        case "place.country_code":
            return row.country_code ?? "";
        case "place.city":
            return row.city ?? "";
        case "place.external_source":
            return row.external_source ?? "";
    }
}
function numericValue(row, field) {
    switch (field) {
        case "place.gross_floor_area_m2":
            return row.gross_floor_area_m2;
        case "place.sub_place_count":
            return row.sub_place_count;
    }
}
function booleanValue(row, field) {
    switch (field) {
        case "place.is_active":
            return row.is_active;
        case "place.has_address":
            return row.gis_location_id != null;
    }
}
// The three condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
            return (c.values ?? []).includes(stringValue(row, c.field));
        case "is_empty":
            return stringValue(row, c.field) === "";
        case "eq":
            return booleanValue(row, c.field) === c.value;
        case "gte":
        case "lte":
        case "between": {
            const v = numericValue(row, c.field);
            if (v == null)
                return false; // an unset number matches no range
            if (c.op === "gte")
                return v >= (c.min ?? Number.NEGATIVE_INFINITY);
            if (c.op === "lte")
                return v <= (c.max ?? Number.POSITIVE_INFINITY);
            return (v >= (c.min ?? Number.NEGATIVE_INFINITY) &&
                v <= (c.max ?? Number.POSITIVE_INFINITY));
        }
    }
}
/**
 * Apply a filter document to index rows (AND of all conditions — v1 `match`
 * is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyPlaceFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=placesFilters.js.map